import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Map as MapIcon, LocationOff } from '@mui/icons-material';
import { CLIENT_DETAILS_DRAWER_WIDTH } from './ClientDetailsDrawer';
import { useMapClientLocations } from '../hooks/useMapClientLocations';
import ClientsLeafletMap from './ClientsLeafletMap';
import { ALL_CUSTOMER_STATUSES, CRM_LABELS, getStatusChipProps } from '../models/Client';

const FAILED_REASON_LABELS = {
  missing: 'Brak kompletnego adresu',
  not_found: 'Nie znaleziono lokalizacji',
};

const MapStatusFilter = ({ selectedStatuses, clients, onToggle, onSelectAll }) => {
  const allSelected = selectedStatuses.size === ALL_CUSTOMER_STATUSES.length;
  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(ALL_CUSTOMER_STATUSES.map((status) => [status, 0]));
    for (const client of clients || []) {
      if (client.status && Object.prototype.hasOwnProperty.call(counts, client.status)) {
        counts[client.status] += 1;
      }
    }
    return counts;
  }, [clients]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Status na mapie
        </Typography>
        <Button
          size="small"
          onClick={onSelectAll}
          disabled={allSelected}
          sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: '0.7rem', textTransform: 'none' }}
        >
          Wszyscy
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {ALL_CUSTOMER_STATUSES.map((status) => {
          const active = selectedStatuses.has(status);
          const chipProps = getStatusChipProps(status, { variant: active ? 'filled' : 'outlined' });
          return (
            <Chip
              key={status}
              size="small"
              label={`${CRM_LABELS.STATUS_LABELS[status]} (${statusCounts[status] ?? 0})`}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => onToggle(status)}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                opacity: active ? 1 : 0.65,
                cursor: 'pointer',
                ...chipProps.sx,
              }}
              {...(chipProps.color ? { color: chipProps.color } : {})}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const ClientsMapPanel = ({ clients, isLoading, onClientSelect, variant = 'sidebar' }) => {
  const isSidebar = variant === 'sidebar';
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set(ALL_CUSTOMER_STATUSES));
  const [failedModalOpen, setFailedModalOpen] = useState(false);

  const {
    geocodeError,
    locations,
    failedCount,
    failedClients,
    isGeocoding,
    progress,
    totalClients,
  } = useMapClientLocations(clients, !isLoading);

  const filteredLocations = useMemo(
    () => locations.filter((location) => selectedStatuses.has(location.status)),
    [locations, selectedStatuses]
  );

  const handleToggleStatus = (status) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleSelectAllStatuses = () => {
    setSelectedStatuses(new Set(ALL_CUSTOMER_STATUSES));
  };

  const mappedCount = filteredLocations.length;
  const showProgress = isGeocoding && progress.total > 0;
  const noneSelected = selectedStatuses.size === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: isSidebar
          ? { md: CLIENT_DETAILS_DRAWER_WIDTH.md, lg: CLIENT_DETAILS_DRAWER_WIDTH.lg }
          : '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        minHeight: 320,
        maxHeight: isSidebar ? 'calc(100vh - 160px)' : 420,
        position: isSidebar ? 'sticky' : 'static',
        top: isSidebar ? 16 : undefined,
        borderLeft: isSidebar ? 1 : 0,
        border: isSidebar ? undefined : 1,
        borderColor: 'divider',
        borderRadius: isSidebar ? '0 12px 12px 0' : 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Mapa klientów
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${mappedCount}${locations.length !== mappedCount ? ` / ${locations.length}` : ''} na mapie`}
            color="primary"
            variant="outlined"
          />
          {failedCount > 0 && (
            <Chip
              size="small"
              label={`${failedCount} bez lokalizacji`}
              variant="outlined"
              onClick={() => setFailedModalOpen(true)}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
      </Box>

      <MapStatusFilter
        selectedStatuses={selectedStatuses}
        clients={clients}
        onToggle={handleToggleStatus}
        onSelectAll={handleSelectAllStatuses}
      />

      {showProgress && (
        <Box sx={{ px: 2, pt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress.total ? (progress.done / progress.total) * 100 : 0}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Wyznaczanie współrzędnych adresów (OpenStreetMap)… {progress.done}/{progress.total}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          position: 'relative',
          minHeight: 280,
        }}
      >
        {isLoading ? (
          <CircularProgress size={36} />
        ) : geocodeError ? (
          <Alert severity="error" sx={{ maxWidth: 420 }}>
            {geocodeError}
          </Alert>
        ) : noneSelected ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
            Zaznacz co najmniej jeden status, aby wyświetlić klientów na mapie.
          </Typography>
        ) : (
          <ClientsLeafletMap locations={filteredLocations} onClientSelect={onClientSelect} />
        )}
      </Box>

      <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Kliknij status powyżej, aby filtrować pinezki. „Wszyscy” — pokaż wszystkie statusy.
          {totalClients > 0 && !isGeocoding && locations.length === 0 && (
            ' Sprawdź, czy klienci mają uzupełniony adres.'
          )}
        </Typography>
      </Box>

      <Dialog
        open={failedModalOpen}
        onClose={() => setFailedModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
          <LocationOff color="warning" />
          Klienci bez lokalizacji ({failedCount})
        </DialogTitle>
        <DialogContent dividers>
          {failedClients.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Brak klientów z problemem lokalizacji.
            </Typography>
          ) : (
            <List dense disablePadding>
              {failedClients.map((client) => (
                <ListItem
                  key={client.id}
                  disablePadding
                  sx={{
                    py: 1,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                    ...(onClientSelect && {
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }),
                  }}
                  onClick={
                    onClientSelect
                      ? () => {
                          onClientSelect(client.id);
                          setFailedModalOpen(false);
                        }
                      : undefined
                  }
                >
                  <ListItemText
                    primary={client.companyName}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          {client.address}
                        </Typography>
                        <Typography component="span" variant="caption" color="warning.main" display="block" sx={{ mt: 0.25 }}>
                          {FAILED_REASON_LABELS[client.reason] || client.reason}
                        </Typography>
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFailedModalOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClientsMapPanel;
